import React, { forwardRef } from 'react';

const TextField = forwardRef(({
  id,
  label,
  type = 'text',
  error,
  helperText,
  fullWidth = false,
  disabled = false,
  required = false,
  variant = 'outlined',
  className = '',
  value,
  onChange,
  placeholder,
  ...props
}, ref) => {
  const baseInputStyles = `
    w-full
    px-4
    py-2.5
    bg-white/5
    backdrop-blur-sm
    border
    rounded-lg
    transition-all
    duration-200
    focus:outline-none
    focus:ring-2
    focus:ring-offset-2
    placeholder:text-gray-400
  `;

  const variantStyles = {
    outlined: `
      border-gray-200
      hover:border-red-300
      focus:border-red-500
      focus:ring-red-500/50
    `,
    filled: `
      border-transparent
      bg-gray-50
      hover:bg-gray-100
      focus:bg-white
      focus:ring-red-500/50
    `,
    glass: `
      border-white/20
      bg-white/10
      hover:bg-white/20
      focus:border-red-500
      focus:ring-red-500/50
    `
  };

  const labelStyles = `
    block
    text-sm
    font-medium
    text-gray-700
    mb-1
  `;

  const errorStyles = `
    border-red-300
    hover:border-red-400
    focus:border-red-500
    focus:ring-red-500/50
    text-red-900
  `;

  const disabledStyles = `
    opacity-50
    cursor-not-allowed
    bg-gray-50
  `;

  const helperTextStyles = `
    mt-1
    text-sm
    ${error ? 'text-red-600' : 'text-gray-500'}
  `;

  const inputStyles = [
    baseInputStyles,
    variantStyles[variant],
    error && errorStyles,
    disabled && disabledStyles,
    fullWidth && 'w-full',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
      {label && (
        <label htmlFor={id} className={labelStyles}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={ref}
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          className={inputStyles}
          {...props}
        />

        {/* Animated focus border effect */}
        <div className="absolute inset-0 rounded-lg pointer-events-none transition-transform duration-200 opacity-0 border-2 border-red-500 scale-105 peer-focus:opacity-100 peer-focus:scale-100" />
      </div>

      {helperText && (
        <p className={helperTextStyles}>
          {helperText}
        </p>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center mt-1 text-sm text-red-600">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}
    </div>
  );
});

TextField.displayName = 'TextField';

export default TextField;
